import { FormikProps } from 'formik';
import { useState, useMemo } from 'react';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { UrlMatchTypeGq } from '../../../../../graphql/projects/models/UrlMatchTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { useDebounceValue } from '../../../../../logic/otherUtilities/useDebounceValue';
import { getLongUrl, getTaskUrl } from '../../Tables/ContentTaskTable/TaskUrl';
import { MetaRuleFormState } from './formLogic/MetaRuleFormState';

export interface UrlInputOptions {
  clientUrl: string;
  project: ProjectGq;
  formBag: FormikProps<MetaRuleFormState>;
}
export type UrlInputBag = ReturnType<typeof useUrlInput>;

export function useUrlInput(options: UrlInputOptions) {
  const { clientUrl, project, formBag } = options;

  // State
  const [showSearchList, setShowSearchList] = useState(false);
  const [page, setPage] = useState(0);

  // Functions
  const cleanedClientUrl = clientUrl?.endsWith('/')
    ? clientUrl.slice(0, clientUrl.length - 1)
    : clientUrl;

  const cleanContentUrl = (url: string) => {
    return url.replace('https://', '').replace('http://', '').toLowerCase()
  };

  const cleanFullUrl = (urlSubString: string) => {
    return getLongUrl(urlSubString.toLowerCase(), cleanedClientUrl)
  };

  const handleSelectUrl = (event: any, selectedUrl: string) => {
    formBag.setFieldValue('url', getTaskUrl(selectedUrl, cleanedClientUrl));
    setShowSearchList(false);
  };

  // Current urls data
  const debouncedSearchTerm = useDebounceValue(formBag.values.url, 200);

  const currentStateUrlsQuery = projectsApiService.queries.useGetMetaUrls({
    searchTerm: debouncedSearchTerm,
    urlMatchType: formBag.values.urlMatchType,
    projectId: project.id,
    page: page,
    rowsPerPage: 10,
  });

  const relevantCurrentUrls = useMemo(() => {
    if (formBag.values.urlMatchType === UrlMatchTypeGq.EXACT) {
      return currentStateUrlsQuery?.allUrls?.filter((analyzedUrl) => cleanContentUrl(analyzedUrl.url) === cleanFullUrl(formBag.values.url))
    } else if (formBag.values.urlMatchType === UrlMatchTypeGq.STARTS) {
      return currentStateUrlsQuery?.allUrls?.filter((analyzedUrl) => cleanContentUrl(analyzedUrl.url).startsWith(cleanFullUrl(formBag.values.url)))
    } else {
      return currentStateUrlsQuery?.allUrls
    }
  }, [currentStateUrlsQuery.allUrls, formBag.values.urlMatchType, formBag.values.url]);

  // Initial urls data
  const initialUrlsQuery = projectsApiService.queries.useGetMetaUrls({
    searchTerm: formBag.initialValues.url,
    projectId: project.id,
    page: page,
    rowsPerPage: 10,
  });

  const relevantInitialUrls = useMemo(() => {
    if (formBag.initialValues.urlMatchType === UrlMatchTypeGq.EXACT) {
      return initialUrlsQuery?.allUrls?.filter((analyzedUrl) => cleanContentUrl(analyzedUrl.url) === cleanFullUrl(formBag.initialValues.url))
    } else if (formBag.initialValues.urlMatchType === UrlMatchTypeGq.STARTS) {
      return initialUrlsQuery?.allUrls?.filter((analyzedUrl) => cleanContentUrl(analyzedUrl.url).startsWith(cleanFullUrl(formBag.initialValues.url)))
    } else {
      return initialUrlsQuery?.allUrls
    }
  }, [initialUrlsQuery.allUrls, formBag.initialValues.urlMatchType, formBag.initialValues.url]);

  return {
    page,
    setPage,
    currentStateUrlsQuery,
    showSearchList,
    setShowSearchList,
    handleSelectUrl,
    cleanedClientUrl,
    clientUrl,
    relevantCurrentUrls,
    relevantInitialUrls,
  };
}
